import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { StoreLink } from "@/components/landing/StoreLinks";

// Mobile-only install bar: dismissable, comes back after more scrolling.
export default function InstallBar() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const dismissedAt = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (dismissedAt.current === null) setVisible(y > 400);
      else if (Math.abs(y - dismissedAt.current) > 900) {
        dismissedAt.current = null;
        setVisible(true);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    dismissedAt.current = window.scrollY;
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          data-testid="mobile-install-bar"
          className="md:hidden fixed left-[74px] right-3 bottom-3 z-40 rounded-[20px] bg-ink text-parchment shadow-journal-lg pl-4 pr-2 py-2.5 flex items-center gap-3"
        >
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold leading-tight">{t.install.title}</div>
            <div className="text-xs text-parchment/70 truncate">{t.install.body}</div>
          </div>
          <StoreLink testId="mobile-install-cta" className="rounded-full bg-gold text-ink px-4 py-2.5 text-sm font-extrabold">
            {t.install.cta}
          </StoreLink>
          <button type="button" onClick={dismiss} aria-label={t.install.dismiss} data-testid="mobile-install-dismiss" className="w-9 h-9 grid place-items-center text-parchment/70">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
